import type { UseQueryResult } from '@tanstack/react-query'

export type CombinedQueriesData<
  TKeys extends readonly string[],
  TResults extends readonly UseQueryResult[]
> = {
  [K in keyof TKeys & `${number}` as TKeys[K]]: K extends keyof TResults
    ? Exclude<TResults[K]['data'], undefined>
    : never
}

export type CombinedQueriesResult<TData> = {
  isPending: boolean
  isFetching: boolean
  isError: boolean
  error: unknown
} & (
  | {
    isSuccess: true
    data: TData
  }
  | {
    isSuccess: false
    data: undefined
  }
)

/**
 * Combines query states and exposes keyed data once every query succeeds.
 * `keys` must match the query results in count and order, as each key maps to the result at the same index.
 */
export const combineQueries = <const TKeys extends readonly string[]>(keys: TKeys) =>
  <TResults extends readonly UseQueryResult[]>(results: TResults): CombinedQueriesResult<CombinedQueriesData<TKeys, TResults>> => {
    if (keys.length !== results.length) {
      throw new Error('Cannot combine queries, keys and results count doesn\'t match.')
    }

    let isPending = false
    let isFetching = false
    let isError = false
    let error: unknown = undefined
    let isSuccess = true

    const data = {} as CombinedQueriesData<TKeys, TResults>

    results.forEach((result, index) => {
      if (result.isPending) {
        isPending = true
      }
      if (result.isFetching) {
        isFetching = true
      }
      if (result.isError) {
        isError = true
        error ??= result.error
      }
      if (!result.isSuccess) {
        isSuccess = false
      } else {
        data[keys[index]!] = result.data
      }
    })

    if (isSuccess) {
      return {
        isPending,
        isFetching,
        isError,
        error,
        isSuccess: true,
        data,
      }
    }

    return {
      isPending,
      isFetching,
      isError,
      error,
      isSuccess: false,
      data: undefined,
    }
  }
